"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportClientException } from "@/lib/analytics/report-client-exception";

type FallbackRender = (context: { reset: () => void }) => ReactNode;

interface ErrorBoundaryProps {
  /**
   * Stable identifier reported to PostHog Error Tracking, e.g.
   * `"email_wall_boundary"`. Helps slice errors by surface in dashboards
   * without grepping component names in source maps.
   */
  source: string;
  /**
   * UI shown when the wrapped tree throws. May be a node, or a render
   * function that receives a `reset()` callback so the fallback can offer
   * an explicit retry button (e.g. "Try again").
   * Default: render nothing.
   */
  fallback?: ReactNode | FallbackRender;
  /**
   * When any value in this array changes between renders, the boundary
   * resets `hasError` back to false. Use to recover after a transient
   * error — e.g. pass `[pathname]` so the boundary retries when the
   * visitor navigates to a new route.
   */
  resetKeys?: readonly unknown[];
  /** Children to render when no error has occurred. */
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

const haveResetKeysChanged = (
  prev: readonly unknown[] | undefined,
  next: readonly unknown[] | undefined
): boolean => {
  const a = prev ?? [];
  const b = next ?? [];
  if (a.length !== b.length) {
    return true;
  }
  for (let i = 0; i < a.length; i++) {
    if (!Object.is(a[i], b[i])) {
      return true;
    }
  }
  return false;
};

/**
 * Generic React error boundary that reports to PostHog and renders a
 * non-blocking fallback. Use to wrap surfaces where a render crash should
 * NOT take down the rest of the page (newsletter forms, ad slots, embeds).
 *
 * Recovery paths so a transient error does not silently delete the wrapped
 * surface for the rest of the page lifetime:
 *  1. Pass `resetKeys` and the boundary will reset when any value changes.
 *  2. Render `fallback` as a function and call the supplied `reset()` from
 *     a "Try again" button.
 *
 * Class component because React 19 still requires `getDerivedStateFromError`
 * + `componentDidCatch` to be class methods — there is no functional API.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientException({
      error,
      source: this.props.source,
      extra: {
        component_stack: info.componentStack ?? null,
      },
    });
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (!this.state.hasError) {
      return;
    }
    if (haveResetKeysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.setState({ hasError: false });
    }
  }

  private readonly handleReset = (): void => {
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallback } = this.props;
      if (typeof fallback === "function") {
        return fallback({ reset: this.handleReset });
      }
      return fallback ?? null;
    }
    return this.props.children;
  }
}
